import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle, ArrowRight } from 'lucide-react';
import { api, getMunicipioSlug } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { emojiFor } from '../utils/solicitacoesHelpers';

/**
 * Widget "O que a cidade está buscando".
 *
 * Render rules:
 *  - Aparece SOMENTE se feature flag widget_cidade_buscando estiver ativa
 *  - Aparece SOMENTE se houver solicitações abertas no município
 *  - Nunca exibe user_id/user_nome/user_whatsapp (usa /v2/solicitacoes/publicas)
 *  - Link CTA "Ver solicitações" só aparece para empreendedor logado
 */
export default function CidadeBuscandoWidget({ municipioSlug: propSlug, limit = 5 }) {
  const slug = propSlug || getMunicipioSlug();
  const { user } = useAuth() || {};
  const { isFeatureEnabled, loading: flagsLoading } = useFeatureFlags();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const fetchPublic = useCallback(async () => {
    if (!slug) return;
    try {
      const r = await api.get('/v2/solicitacoes/publicas', { params: { municipio_slug: slug, limit } });
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoaded(true);
    }
  }, [slug, limit]);

  useEffect(() => {
    if (flagsLoading) return;
    if (!isFeatureEnabled('widget_cidade_buscando')) {
      setLoaded(true);
      return;
    }
    fetchPublic();
  }, [flagsLoading, isFeatureEnabled, fetchPublic]);

  if (flagsLoading || !loaded) return null;
  if (!isFeatureEnabled('widget_cidade_buscando')) return null;
  if (!items || items.length === 0) return null;

  // identifica se user é empreendedor (heurística simples: tem business OU role inclui 'empreend')
  const isEmpreendedor = !!user && (
    (user.role && /empreend/i.test(user.role)) ||
    user.has_business === true
  );

  return (
    <section className="my-8" data-testid="widget-cidade-buscando">
      <div className="bg-gradient-to-br from-amber-50 via-white to-orange-50 rounded-2xl p-5 md:p-6 border border-amber-100">
        <header className="flex items-center justify-between mb-3">
          <h2 className="text-lg md:text-xl font-bold flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-amber-600" /> O que a cidade está buscando
          </h2>
        </header>
        <ul className="space-y-2">
          {items.map((it) => (
            <li
              key={it.id}
              data-testid={`widget-buscando-item-${it.id}`}
              className="flex items-start gap-2 text-sm text-gray-800"
            >
              <span className="text-lg shrink-0">{emojiFor(it.categoria)}</span>
              <span>Alguém {(it.texto || '').toLowerCase()}</span>
            </li>
          ))}
        </ul>
        {isEmpreendedor && (
          <div className="mt-4">
            <Link
              to="/meus-negocios"
              data-testid="widget-buscando-cta"
              className="inline-flex items-center gap-1 text-sm font-medium text-amber-700 hover:text-amber-900"
            >
              Você pode ajudar? Ver solicitações <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
